import type { SkillAnalyticsPageDto, SkillScope, SkillUsageRowDto } from '@dbrain/shared';
import { Button, Icons, Progress, Skeleton } from '@dbrain/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { DataTable, FilterMenu, Pagination, PeriodPill, SearchBox, sortState, useListParams, type Column } from '../../components/admin/DataTable.js';
import { ErrorAlert } from '../../components/ErrorAlert.js';
import { formatCount, formatPercent, formatTokens, lastSignIn } from '@dbrain/app-core/format';
import { useAdminDomains } from './AccessPage.js';
import { AdminLayout } from './AdminLayout.js';

/**
 * Yönetim → Skill analitiği (YonetimSkill.dc.html, 06 §23.6): which skills
 * are loaded, by the model or by name, failures (they are in the audit log
 * too), people and the tokens a skill adds. Members' own skills are one
 * nameless row. No conversation content.
 */
const DESC = new Set(['loads', 'model', 'failed', 'users', 'tokens', 'lastUsed']);
const SCOPES: SkillScope[] = ['builtin', 'organization', 'team', 'personal'];

/** the library pages' tag colours (D-118): Ekip pink, Herkes blue, members' own purple, Standart grey */
const TAG: Record<SkillScope, string> = { team: 'team', organization: 'organization', personal: 'user', builtin: 'builtin' };

function ScopeChip({ row }: { row: Pick<SkillUsageRowDto, 'scope' | 'domain'> }) {
  const { t } = useTranslation();
  return (
    <span className={`scope-tag scope-tag--${TAG[row.scope]} scope-chip scope-chip--${row.scope}`}>
      {row.scope === 'team' && row.domain ? t('admin.skills.domainChip', { name: row.domain.name }) : t(`admin.skills.scopes.${row.scope}`)}
    </span>
  );
}

function LastUsed({ at }: { at: string | null }) {
  const { t, i18n } = useTranslation();
  const value = lastSignIn(at, i18n.language, new Date(), useCompanyZone());
  if (value.kind === 'never') return <span className="muted">—</span>;
  if (value.kind === 'today') return <>{t('admin.skills.lastUsed.today')}</>;
  if (value.kind === 'yesterday') return <>{t('admin.skills.lastUsed.yesterday')}</>;
  if (value.kind === 'days') return <>{t('admin.skills.lastUsed.days', { count: value.days })}</>;
  return <>{value.date}</>;
}

export function SkillAnalyticsPage() {
  const { t, i18n } = useTranslation();
  const list = useListParams<'scope' | 'domain'>();
  const days = Number(list.get('days') ?? 30) || 30;
  const { sort, onSort } = sortState(list, 'loads', (key) => DESC.has(key));
  const query = list.query();
  const analytics = useQuery({ queryKey: ['admin', 'skills', query], queryFn: () => api<SkillAnalyticsPageDto>('GET', `/api/admin/skills?${query}`), placeholderData: keepPreviousData });
  const domains = useAdminDomains();
  const [openUnused, setOpenUnused] = useState(false);
  const data = analytics.data;
  const auditDays = days === 1 ? 1 : days <= 7 ? 7 : days <= 30 ? 30 : 90;
  const most = Math.max(1, ...(data?.rows.map((r) => r.loads) ?? [1]));
  const nameOf = (row: SkillUsageRowDto) => (row.scope === 'personal' ? t('admin.skills.personalRow') : (row.name ?? row.key));
  const columns: Column<SkillUsageRowDto>[] = [
    {
      id: 'skill',
      header: t('admin.skills.columns.skill'),
      sort: 'name',
      cell: (row) => (
        <span className="skill-cell">
          <span className={row.scope === 'personal' ? 'skill-cell__name' : 'skill-cell__name skill-cell__name--mono'}>{nameOf(row)}</span>
          <ScopeChip row={row} />
        </span>
      ),
    },
    {
      id: 'loads',
      header: t('admin.skills.columns.loads'),
      width: 'md',
      numeric: true,
      sort: 'loads',
      cell: (row) => (
        <span className="bar-cell">
          <Progress value={row.loads} max={most} label={t('admin.skills.columns.loads')} tone="running" />
          <span className="bar-cell__number">{formatCount(row.loads, i18n.language)}</span>
        </span>
      ),
    },
    {
      id: 'how',
      header: t('admin.skills.columns.how'),
      width: 'lg',
      sort: 'model',
      cell: (row) => (
        <span className="bar-cell bar-cell--split">
          <Progress value={row.byModel} max={Math.max(1, row.loads)} label={t('admin.skills.byModel', { percent: formatPercent(row.byModel, row.loads, i18n.language) })} tone="success" />
          <span className="bar-cell__text">{t('admin.skills.byModel', { percent: formatPercent(row.byModel, row.loads, i18n.language) })}</span>
        </span>
      ),
    },
    {
      id: 'failed',
      header: t('admin.skills.columns.failed'),
      width: 'sm',
      numeric: true,
      sort: 'failed',
      cell: (row) =>
        row.failed > 0 ? (
          <Link
            className="failed-link"
            to={`/admin/audit?action=skill.load_failed&days=${auditDays}${row.scope === 'personal' || !row.name ? '' : `&q=${encodeURIComponent(row.name)}`}`}
            data-testid="skill-failed-link"
          >
            {formatCount(row.failed, i18n.language)}
          </Link>
        ) : (
          <span className="muted">0</span>
        ),
    },
    { id: 'users', header: t('admin.skills.columns.users'), width: 'xs', numeric: true, sort: 'users', cell: (row) => formatCount(row.users, i18n.language) },
    { id: 'tokens', header: t('admin.skills.columns.tokens'), width: 'sm', numeric: true, sort: 'tokens', cell: (row) => formatTokens(row.bodyTokens, i18n.language) },
    { id: 'lastUsed', header: t('admin.skills.columns.lastUsed'), width: 'md', sort: 'lastUsed', cell: (row) => <LastUsed at={row.lastUsed} /> },
  ];
  return (
    <AdminLayout section="skills" title={t('admin.sections.skills')} actions={<PeriodPill days={days} onChange={(value) => list.set({ days: value === 30 ? null : String(value) })} />}>
      <div className="stack admin-skills">
        <ErrorAlert error={analytics.error} />
        {data ? (
          <div className="stat-cards">
            <div className="stat-card" data-testid="skills-card-loads">
              <p className="stat-card__label">{t('admin.skills.cards.loads')}</p>
              <p className="stat-card__value">{formatCount(data.stats.loads, i18n.language)}</p>
              <p className="stat-card__sub">{t('admin.skills.cards.loadsSub', { skills: formatCount(data.stats.skills, i18n.language), people: formatCount(data.stats.people, i18n.language) })}</p>
            </div>
            <div className="stat-card" data-testid="skills-card-model">
              <p className="stat-card__label">{t('admin.skills.cards.model')}</p>
              <p className="stat-card__value">{formatPercent(data.stats.byModel, data.stats.loads, i18n.language)}</p>
              <p className="stat-card__sub">{t('admin.skills.cards.modelSub', { percent: formatPercent(data.stats.loads - data.stats.byModel, data.stats.loads, i18n.language) })}</p>
            </div>
            <div className="stat-card stat-card--danger" data-testid="skills-card-failed">
              <p className="stat-card__label">{t('admin.skills.cards.failed')}</p>
              <p className="stat-card__value">{formatCount(data.stats.failed, i18n.language)}</p>
              <p className="stat-card__sub">
                <Link to={`/admin/audit?action=skill.load_failed&days=${auditDays}`}>{t('admin.skills.cards.failedSub', { percent: formatPercent(data.stats.failed, data.stats.loads, i18n.language, 1) })}</Link>
              </p>
            </div>
            <div className="stat-card stat-card--warning" data-testid="skills-card-unused">
              <p className="stat-card__label">{t('admin.skills.cards.unused')}</p>
              <p className="stat-card__value">{formatCount(data.unused.length, i18n.language)}</p>
              <p className="stat-card__sub">{t('admin.skills.cards.unusedSub')}</p>
            </div>
          </div>
        ) : (
          <Skeleton block />
        )}
        <div className="dt-toolbar">
          <SearchBox value={list.get('q') ?? ''} onChange={(q) => list.set({ q })} placeholder={t('admin.skills.search')} testId="skills-search" />
          <FilterMenu
            label={t('admin.skills.scope')}
            value={list.get('scope')}
            allLabel={t('admin.access.all')}
            options={SCOPES.map((scope) => ({ value: scope, label: t(`admin.skills.scopes.${scope}`) }))}
            onChange={(scope) => list.set({ scope })}
            testId="skills-scope"
          />
          <FilterMenu
            label={t('admin.access.domain')}
            value={list.get('domain')}
            allLabel={t('admin.access.all')}
            options={(domains.data?.rows ?? []).map((d) => ({ value: d.id, label: d.name }))}
            onChange={(domain) => list.set({ domain })}
            testId="skills-domain"
          />
          <span className="dt-toolbar__end muted">{t('admin.skills.privacy')}</span>
        </div>
        {data ? (
          <DataTable
            label={t('admin.sections.skills')}
            columns={columns}
            rows={data.rows}
            rowKey={(row) => row.key}
            sort={sort}
            onSort={onSort}
            rowTestId={(row) => `skill-row-${row.name ?? row.key}`}
            loading={analytics.isFetching}
            empty={t('skillAnalytics.empty')}
            data-testid="skill-analytics"
            footer={
              <Pagination
                page={list.page}
                size={list.size}
                total={data.total}
                noun={t('admin.skills.noun')}
                note={sort.key === 'loads' && sort.desc ? t('admin.skills.note') : undefined}
                onPage={(page) => list.set({ page: String(page) })}
                onSize={(size) => list.set({ size: String(size) })}
              />
            }
          />
        ) : null}
        {data && data.unused.length > 0 ? (
          <section className="unused-warning" data-testid="skills-unused">
            <p className="unused-warning__line">
              <Icons.warning size={16} />
              <span>
                <strong>{t('admin.skills.unusedWarning', { count: data.unused.length, days })}</strong>{' '}
                <span className="mono-list">{data.unused.map((u) => u.name).join(', ')}</span>
              </span>
              <Button size="sm" variant="secondary" aria-expanded={openUnused} onClick={() => setOpenUnused((v) => !v)} data-testid="skills-unused-toggle">
                {openUnused ? t('admin.skills.closeList') : t('admin.skills.openList')}
              </Button>
            </p>
            {openUnused ? (
              <ul className="unused-warning__list">
                {data.unused.map((u) => (
                  <li key={u.id}>
                    <code>{u.name}</code> <ScopeChip row={{ scope: u.scope, domain: u.domain }} />
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
    </AdminLayout>
  );
}
